/**
 * The selling price to show for a product, in the shopkeeper's own terms.
 *
 * `product.sellingPrice` is stored in the store's canonical billing convention
 * (TAX_INCLUSIVE). That is not necessarily the convention the price was typed
 * in: a shop that stores exclusive while entering "With GST" types 1450 for an
 * 18% item and gets 1228.81 written to the row. The inventory list showed that
 * 1228.81 — a number the shopkeeper never typed and cannot match to their own
 * shelf label, which reads like the app quietly changed their price.
 *
 * ProductForm already inverts this when opening a product to edit. This wraps
 * the same inversion for read-only surfaces so the list, the detail panel and
 * the form all agree.
 *
 * Kept out of billing/priceMode.ts on purpose: that module is pure arithmetic
 * with no store dependency, and the billing engine relies on it staying that
 * way. Reading settings synchronously matches ProductForm, which does the same;
 * price-entry mode is a shop-level preference that does not change mid-session.
 */

import { displaySellingPrice } from '@/billing/priceMode';
import { useSettingsStore } from '@/store/useSettingsStore';

interface PricedProduct {
  sellingPrice: number;
  gstRate: number;
  cessRate: number;
}

export function productDisplayPrice(product: PricedProduct): number {
  const s = useSettingsStore.getState();
  return displaySellingPrice(
    product.sellingPrice,
    product.gstRate,
    product.cessRate ?? 0,
    s.getBoolean('PRODUCT_PRICE_INCLUDES_GST'),
    s.getBoolean('TAX_INCLUSIVE'),
  );
}
